<template>
  <div v-if="hasError" class="error-fallback">
    <div class="error-content">
      <div class="error-icon">🔄</div>
      <h2>Something's not working right</h2>
      <p>We're having trouble loading this content. Please try refreshing the page.</p>
      <div class="error-actions">
        <button @click="reload" class="btn btn-primary">
          Refresh Page
        </button>
        <button @click="goHome" class="btn btn-secondary">
          Go to Home
        </button>
      </div>
    </div>
  </div>
  <slot v-else />
</template>

<script>
export default {
  name: 'ErrorBoundary',
  data() {
    return {
      hasError: false
    };
  },
  errorCaptured(error, instance, info) {
    // Log error details for debugging (not shown to user)
    console.error('Error caught by boundary:', error);
    console.error('Component info:', info);

    this.hasError = true;

    // Report error to logging service
    this.reportError(error, info);

    // Prevent the error from propagating further
    return false;
  },
  mounted() {
    // Global error handler for unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);

    // Global error handler for JavaScript errors
    window.addEventListener('error', this.handleGlobalError);
  },
  beforeUnmount() {
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
    window.removeEventListener('error', this.handleGlobalError);
  },
  methods: {
    handleUnhandledRejection(event) {
      console.error('Unhandled promise rejection:', event.reason);

      // Only show error boundary for critical failures
      if (this.isCriticalError(event.reason)) {
        this.hasError = true;
      }

      this.reportError(event.reason, 'Unhandled Promise Rejection');
      event.preventDefault();
    },

    handleGlobalError(event) {
      console.error('Global JavaScript error:', event.error);

      // Only show error boundary for critical failures
      if (this.isCriticalError(event.error)) {
        this.hasError = true;
      }

      this.reportError(event.error, 'Global Error');
      event.preventDefault();
    },

    isCriticalError(error) {
      // Only show error boundary for truly critical failures
      // Ignore network errors, auth errors, etc. that should be handled elsewhere
      const message = error?.message?.toLowerCase() || '';

      // Don't show error boundary for these types of errors
      if (message.includes('network') ||
          message.includes('fetch') ||
          message.includes('unauthorized') ||
          message.includes('not found') ||
          message.includes('timeout')) {
        return false;
      }

      // Show error boundary for JavaScript errors, component failures
      return true;
    },

    reportError(error, context) {
      // Report to logging service without exposing to user
      const errorReport = {
        message: error?.message || 'Unknown error',
        stack: error?.stack,
        context,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        url: window.location.href
      };

      // TODO: Send to error reporting service
      console.error('Error Report:', errorReport);

      // Example integrations:
      // if (window.Sentry) {
      //   window.Sentry.captureException(error);
      // }
      // if (window.LogRocket) {
      //   window.LogRocket.captureException(error);
      // }
    },

    reload() {
      window.location.reload();
    },

    goHome() {
      this.$router.push('/').catch(() => {
        window.location.href = '/';
      });
    }
  }
};
</script>

<style scoped>
.error-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  padding: 20px;
}

.error-content {
  text-align: center;
  max-width: 400px;
  background: white;
  padding: 40px 30px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
  opacity: 0.7;
}

.error-content h2 {
  color: #333;
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 600;
}

.error-content p {
  color: #666;
  margin-bottom: 30px;
  font-size: 16px;
  line-height: 1.5;
}

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .error-content {
    padding: 30px 20px;
    margin: 10px;
  }

  .error-actions {
    flex-direction: column;
    align-items: center;
  }

  .btn {
    width: 100%;
    max-width: 200px;
  }
}
</style>